import { Header } from "@/components/Header";
import { PremiumFooter } from "@/components/landing/PremiumFooter";
import { Sparkles, Zap, Bug, Star } from "lucide-react";

const changelogData = [
  {
    version: "1.2.0",
    date: "3 décembre 2024",
    title: "Système de crédits & Dashboard",
    changes: [
      { type: "feature", text: "Nouveau système de crédits avec historique des transactions" },
      { type: "feature", text: "Dashboard utilisateur complet avec sidebar navigation" },
      { type: "feature", text: "Page Inspiration avec prompts suggérés" },
      { type: "feature", text: "Page Facturation avec historique des paiements" },
      { type: "improvement", text: "Amélioration des performances de génération" },
      { type: "improvement", text: "Nouveau design du calculateur de crédits" },
    ],
  },
  {
    version: "1.1.0",
    date: "28 novembre 2024",
    title: "Landing page premium",
    changes: [
      { type: "feature", text: "Nouvelle landing page professionnelle" },
      { type: "feature", text: "Section galerie avec filtres par catégorie" },
      { type: "feature", text: "Page tarification avec plans détaillés" },
      { type: "feature", text: "FAQ interactive" },
      { type: "improvement", text: "Animations et transitions améliorées" },
      { type: "fix", text: "Correction du responsive sur mobile" },
    ],
  },
  {
    version: "1.0.0",
    date: "15 novembre 2024",
    title: "Lancement officiel",
    changes: [
      { type: "feature", text: "Génération d'images Floop Basic" },
      { type: "feature", text: "Génération d'images Floop Pro (qualité studio)" },
      { type: "feature", text: "Mode Text-to-Image" },
      { type: "feature", text: "Mode Image-to-Image avec contrôle de force" },
      { type: "feature", text: "Historique des générations" },
      { type: "feature", text: "Téléchargement des images générées" },
    ],
  },
  {
    version: "0.9.0 (Beta)",
    date: "1 novembre 2024",
    title: "Version Beta",
    changes: [
      { type: "feature", text: "Interface de génération d'images" },
      { type: "feature", text: "Intégration des modèles IA Floop" },
      { type: "feature", text: "Sélection de résolution (512px à 4K)" },
      { type: "improvement", text: "Tests de performance et optimisations" },
      { type: "fix", text: "Correction de bugs divers" },
    ],
  },
];

const getChangeIcon = (type: string) => {
  switch (type) {
    case "feature":
      return <Sparkles className="w-4 h-4 text-primary" />;
    case "improvement":
      return <Zap className="w-4 h-4 text-accent" />;
    case "fix":
      return <Bug className="w-4 h-4 text-highlight" />;
    default:
      return <Star className="w-4 h-4 text-muted-foreground" />;
  }
};

const getChangeLabel = (type: string) => {
  switch (type) {
    case "feature":
      return "Nouveau";
    case "improvement":
      return "Amélioration";
    case "fix":
      return "Correction";
    default:
      return "Autre";
  }
};

const Changelog = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="pt-24 pb-12 text-center">
        <div className="container mx-auto px-6">
          <span className="inline-block text-sm font-medium text-primary mb-4">
            Changelog
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 font-display">
            Quoi de neuf sur{" "}
            <span className="bg-gradient-floop bg-clip-text text-transparent">
              Floop
            </span>
            ?
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Suivez l'évolution de Floop. Nouvelles fonctionnalités, améliorations et corrections.
          </p>
        </div>
      </section>

      {/* Changelog List */}
      <section className="pb-16">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="space-y-12">
            {changelogData.map((release, index) => (
              <div key={index} className="relative">
                {/* Version Badge */}
                <div className="flex items-center gap-4 mb-4">
                  <span className="px-3 py-1 bg-gradient-floop text-white text-sm font-semibold rounded-full">
                    v{release.version}
                  </span>
                  <span className="text-sm text-muted-foreground">{release.date}</span>
                </div>

                {/* Content Card */}
                <div className="bg-card border border-border rounded-2xl p-6">
                  <h2 className="text-xl font-bold text-foreground mb-4">{release.title}</h2>
                  <ul className="space-y-3">
                    {release.changes.map((change, changeIndex) => (
                      <li key={changeIndex} className="flex items-start gap-3">
                        <div className="mt-0.5">{getChangeIcon(change.type)}</div>
                        <div className="flex-1">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded mr-2 ${
                            change.type === "feature" ? "bg-primary/20 text-primary" :
                            change.type === "improvement" ? "bg-accent/20 text-accent" :
                            "bg-highlight/20 text-highlight"
                          }`}>
                            {getChangeLabel(change.type)}
                          </span>
                          <span className="text-muted-foreground">{change.text}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Timeline connector */}
                {index < changelogData.length - 1 && (
                  <div className="absolute left-6 top-full h-12 w-px bg-border" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <PremiumFooter />
    </div>
  );
};

export default Changelog;
